import React, { useState } from "react";
import { Sparkles, LogOut, User, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useAccess } from "@/context/AccessContext";
import { ProfileDialog } from "./ProfileDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function badgeLabels({ kind, status, daysLeft }) {
  if (kind !== "subscription") return { badge: `Pass · ${daysLeft}d left`, shortBadge: `Pass ${daysLeft}d` };
  if (status === "trialing") return { badge: `Trial · ${daysLeft}d left`, shortBadge: `Trial ${daysLeft}d` };
  if (status === "past_due") return { badge: "Payment failed", shortBadge: "Fix payment" };
  return { badge: "Subscriber", shortBadge: "Subscriber" };
}

export function Header({ onScrollTo }) {
  const { user, logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const { active, kind, status, loading: accessLoading, daysLeft, price_usd, trial_days, openAuth } = useAccess();
  const { badge, shortBadge } = badgeLabels({ kind, status, daysLeft });

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 bg-ink/70 backdrop-blur-xl backdrop-saturate-150 border-b border-white/5"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-3 px-4 sm:px-8 lg:px-12 py-3 sm:py-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative shrink-0">
            <div className="w-9 h-9 rounded-full bg-pb_red shadow-[0_0_14px_rgba(229,9,20,0.6)] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
          </div>
          <div className="min-w-0">
            <div className="font-heading text-xl leading-none tracking-tight text-parchment whitespace-nowrap">
              Powerball <span className="italic text-oracle">Oracle</span>
            </div>
            <div className="label-caps mt-1 hidden sm:block">Analytics · Prediction · Divination</div>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-8 label-caps text-parchment/70">
          <button data-testid="nav-oracle" onClick={() => onScrollTo?.("oracle")} className="hover:text-oracle transition-colors">Oracle</button>
          {active && <button data-testid="nav-analytics" onClick={() => onScrollTo?.("analytics")} className="hover:text-oracle transition-colors">Analytics</button>}
          {active && <button data-testid="nav-history" onClick={() => onScrollTo?.("history")} className="hover:text-oracle transition-colors">History</button>}
          {active && (
            <button data-testid="nav-picks" onClick={() => onScrollTo?.("picks")} className="hover:text-oracle transition-colors">My Picks</button>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {!accessLoading && (active ? (
            <button data-testid="pass-status-badge" onClick={() => setProfileOpen(true)} className="inline-flex items-center gap-1.5 label-caps text-oracle border border-oracle/30 rounded-full px-2.5 sm:px-3 py-1.5 bg-oracle/5 hover:bg-oracle/15 transition-colors whitespace-nowrap">
              <Ticket className="w-3 h-3" strokeWidth={1.5} />
              <span className="sm:hidden">{shortBadge}</span>
              <span className="hidden sm:inline">{badge}</span>
            </button>
          ) : (
            <Button
              data-testid="header-unlock-btn"
              onClick={() => onScrollTo?.("oracle")}
              variant="ghost"
              className="hidden sm:inline-flex text-oracle hover:bg-oracle/10 label-caps"
            >
              <Ticket className="w-4 h-4 mr-2" strokeWidth={1.5} /> {trial_days}-day free trial · then ${price_usd.toFixed(2)}/mo
            </Button>
          ))}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="user-menu-btn"
                  variant="ghost"
                  className="text-parchment hover:text-oracle hover:bg-white/5 font-body px-2 sm:px-4"
                >
                  <User className="w-4 h-4 sm:mr-2" strokeWidth={1.5} /> <span className="hidden sm:inline max-w-[160px] truncate">{user.name || user.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-surface border-white/10 text-parchment">
                <DropdownMenuLabel className="font-body text-xs text-parchment/60">
                  Signed in as {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="profile-item" onClick={() => setProfileOpen(true)} className="focus:bg-white/5 focus:text-oracle">
                  <Ticket className="w-4 h-4 mr-2" /> My subscription & billing
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-item" onClick={logout} className="focus:bg-white/5 focus:text-oracle">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              data-testid="open-auth-btn"
              onClick={openAuth}
              className="bg-oracle text-ink hover:bg-[#FFC033] font-body uppercase tracking-wider text-xs shadow-[0_0_20px_rgba(255,176,0,0.2)] hover:shadow-[0_0_30px_rgba(255,176,0,0.4)] transition-all"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} email={user?.email || "Guest — this browser"} />
    </header>
  );
}
